import {Button, Checkbox, Divider, Form, Input, InputNumber, Modal, notification, Space} from "antd";
import BMButton, {BMButtonProps} from "../BMButton";
import React, {useCallback, useContext, useEffect, useState} from "react";
import TextArea from "antd/es/input/TextArea";
import {DEFAULT_THROTTLE, PilotInspectionFee} from "../../api/models";
import BMReadOnly from "../BMReadOnly";
import {FormProps} from "antd/es/form/Form";
import {useIntl} from "react-intl";
import {ConfiguratorContext} from "../../context";
import _ from "lodash";
import Utils from "../../util/util";

export const PilotInspectionFeeLabel = "Pilot Inspection Fee";

interface PilotInspectionFeeFormValues {
    id?: string
    key: string
    label: string
    notes?: string | undefined
    value: number
    truckCnt: number
    visitorCnt: number
    includeTravel: boolean
}

export const defaultPilotInspectionFeeValues = {
    truckCnt: 1,
    visitorCnt: 1,
    includeTravel: true,
    label: PilotInspectionFeeLabel,
    value: 0,
}

const PilotInspectionFeeButtonModal = (props: Omit<BMButtonProps, "value" | "onChange"> & {
    value?:PilotInspectionFee
    quantity: number
    onChange?: (v: PilotInspectionFee | undefined) => void
}) => {

    const { id, value, quantity, onChange, title, ...btnProps } = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [form] = Form.useForm();
    const [initialValues, setInitialValues] = useState<PilotInspectionFeeFormValues>();

    useEffect(() => {
        form.resetFields();
    }, [initialValues])

    const handleOpen = async () => {
        const value = {
            key: crypto.randomUUID(),
            ...defaultPilotInspectionFeeValues,
            ...props.value
        };
        setInitialValues(value);
    }

    const handleCancelBtn = () => {
        setIsOpen(false);
    }

    const handleDeleteBtn = () => {
        setIsOpen(false);

        props.onChange?.(undefined);
    }

    const handleOkBtn = async () => {
        try {
            const values = await form.validateFields() as PilotInspectionFeeFormValues;
            setIsOpen(false);

            const pilotInspectionOption : PilotInspectionFee = {
                ...defaultPilotInspectionFeeValues,
                ...values
            };

            props.onChange?.(pilotInspectionOption);
        }
        catch(e:any) {
            //already visible in the form
        }
    }

    //hide border when disabled
    const btnStyle =  ( props.disabled )
        ? {borderBottom: "none", color: "black", ...props.style}
        : {borderBottom: "1px solid black", ...props.style};

    const btnLbl =  props.value ? PilotInspectionFeeLabel : "Add " + PilotInspectionFeeLabel;

    const modalTitle = `${ (props.value) ? "Edit" : "Add" } ${PilotInspectionFeeLabel}`

    return <>
        <BMButton type="text"
                  className="ghostBmButton"
                  {...btnProps}
                  title={btnLbl}
                  onClick={() => setIsOpen(true)}
                  style={{...props.style, padding: "0"}}
        ><span style={btnStyle}>{btnLbl}</span></BMButton>

        <Modal
            title={modalTitle}
            open={isOpen}
            onOk={handleOkBtn}
            onCancel={handleCancelBtn}
            footer={<div style={{display: "flex", justifyContent: "space-between", flexDirection: "row-reverse"}}>
                <Space>
                    <Button key="cancelBtn" onClick={handleCancelBtn}>Cancel</Button>
                    <Button key="doneBtn" onClick={handleOkBtn} type="primary">Done</Button>
                </Space>
                {!!props.value && <Button key="deleteBtn" className="deleteBtn" onClick={handleDeleteBtn} danger>Remove</Button>}
            </div>}
            afterOpenChange={(open) => {
                if( open ) {
                    handleOpen();
                }
            }}
        >
            <PilotInspectionFeeForm
                form={form}
                initialValues={initialValues}
                quantity={quantity}
            />

        </Modal>

    </>;
}

export const PilotInspectionFeeForm = (props:FormProps & {
    quantity: number
}) => {

    const intl = useIntl();
    const configurator = useContext(ConfiguratorContext);

    const {quantity, ...formProps} = props;

    const handleValuesChange = useCallback( _.throttle( async (changed:PilotInspectionFeeFormValues, all: PilotInspectionFeeFormValues) => {

        if ( 'includeTravel' in changed || 'visitorCnt' in changed || 'truckCnt' in changed ) {
            const totalFee = await calculateFee( all );
            if ( totalFee !== undefined ) {
                const perUnitFee = totalFee / quantity;
                props.form?.setFieldValue("totalFee", totalFee);
                props.form?.setFieldValue("value", perUnitFee);
            }
        }

        props.onValuesChange?.(changed, all);

    }, DEFAULT_THROTTLE ), [] );

    useEffect(() => {
        if( props.initialValues ) {
            handleValuesChange(props.initialValues as PilotInspectionFee, props.initialValues as PilotInspectionFee);
        }
    }, [props.initialValues]);

    const calculateFee = async (value:PilotInspectionFeeFormValues): Promise<number | undefined> => {
        try {
            const resp = await configurator.api.calculatePilotInspectionFee( value );
            if ( resp.data ) {
                return resp.data.value;
            }
        }
        catch (e:any) {
            const id = e.response?.data?.message || e.message ;
            const errorMsg = intl.formatMessage({ id });
            notification.error( { message: "Failed to calculate the pilot inspection fee. " + errorMsg });
        }

        return;
    }

    const validateVisitorCnt = async (rule: any, value: string, cb: any) => {
        const val = Number( value );
        if ( isNaN(val) ) {
            throw new Error("Invalid number");
        }

        if ( val < 1 ) {
            throw new Error("Required")
        }
    };

    const validateTruckCnt = async (rule: any, value: string, cb: any) => {
        const val = Number( value );
        if ( isNaN(val) ) {
            throw new Error("Invalid number");
        }

        if ( val < 1 ) {
            throw new Error("Required")
        }

        if ( val > quantity ) {
            throw new Error("Exceeds Quantity")
        }
    };

    return <Form {...formProps}
                 onValuesChange={handleValuesChange} >
        <Form.Item name="id" label="id" hidden={true} >
            <Input />
        </Form.Item>

        <Form.Item name="key" label="key" hidden={true} >
            <Input />
        </Form.Item>

        <Form.Item name="label" label="label" hidden={true} >
            <Input />
        </Form.Item>

        <div style={{display: "flex", alignItems:"baseline", gap: "0.4rem"}}>
            <Form.Item name="truckCnt" label="Inspect"
                       rules={[{
                           required: true,
                           validator: validateTruckCnt,
                       }]}>
                <InputNumber />
            </Form.Item>
            <div>of {quantity} trucks</div>
        </div>

        <div style={{display: "flex", gap: "2rem"}}>
        <Form.Item name="visitorCnt" label="Visitors"
            rules={[{
            required: true,
            validator: validateVisitorCnt
        }]}>
            <InputNumber />
        </Form.Item>

        <Form.Item name="includeTravel" valuePropName="checked" >
            <Checkbox>Include Travel</Checkbox>
        </Form.Item>
        </div>

        <Form.Item name="notes" label="Notes" colon >
            <TextArea placeholder="Provide any additional information." rows={2} />
        </Form.Item>

        <Divider />

        <Form.Item name="totalFee" label="Pilot Inspection Fee"
                   rules={[{
                       required: true,
                       validator: validateVisitorCnt
                   }]}>
            <BMReadOnly readOnly={true} renderValue={Utils.formatMoney} >
                <Input />
            </BMReadOnly>
        </Form.Item>

        <Form.Item name="value" label={`Pilot Inspection Fee Per ${quantity} ${quantity > 1 ? "units" : "unit"}`}
                   rules={[{
                       required: true,
                       validator: validateVisitorCnt
                   }]}>
            <BMReadOnly readOnly={true} renderValue={Utils.formatMoney} >
                <Input />
            </BMReadOnly>
        </Form.Item>

    </Form>
}

export default PilotInspectionFeeButtonModal;

